import classNames from "classnames";
import { ReactComponent as BellIcon } from "@icons/bell.svg";
import { ReactComponent as ErrorIcon } from "@icons/error-alert.svg";
import { Notification } from "shared/request/myHealthyAdvantageApi";
import { timeSince } from "core/helpers/dates";
import { useTranslation } from "react-i18next";
import Spinner from "@brighthr/component-spinner";
import { AxiosError } from "axios";
import { IResponseError } from "shared/request/IResponseError";
import { Container } from "shared/UI/LayoutComponents/LayoutComponents";

export type NotificationsListProps = {
  delayedLoading: boolean;
  data: Notification[];
  visibleData: Notification[];
  handleArchive: (notification: Notification) => void;
  newNotificationsErrors?: AxiosError<IResponseError>;
}

export default function NotificationsList({
  delayedLoading,
  data,
  visibleData,
  handleArchive,
  newNotificationsErrors,
}: NotificationsListProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "notifications" });

  function groupNotifications(notifications: Notification[]) {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(startOfToday);
    startOfWeek.setDate(startOfToday.getDate() - startOfToday.getDay());

    return {
      today: notifications.filter(n => new Date(n.createdDate) >= startOfToday),
      week: notifications.filter(n => {
        const created = new Date(n.createdDate);
        return created < startOfToday && created >= startOfWeek;
      }),
      earlier: notifications.filter(n => new Date(n.createdDate) < startOfWeek),
    };
  }

  if (newNotificationsErrors) {
    return (
      <Container>
        <div className="select-none h-full w-full flex items-center justify-center flex-col gap-4 pt-16 pb-32 px-4">
          <ErrorIcon className="h-16 w-16 text-brand-red-lighter" />
          <h3 className="text-center">{newNotificationsErrors?.response ? t("genericError") : t("networkError")}</h3>
        </div>
      </Container>
    );
  }

  const isEmpty = !delayedLoading && (!data || data.length === 0);

  if (isEmpty) {
    return (
      <div className="select-none h-full w-full flex items-center justify-center flex-col gap-4 pt-16 pb-32">
        <BellIcon className="h-16 w-16 text-navy-blue-lighter" />
        <h3 className="text-center">{t("noNotifications")}</h3>
      </div>
    );
  }

  function renderNotification(notification: Notification) {
    return (
      <button
        key={notification.id}
        onClick={() => handleArchive(notification)}
        className={classNames(
          "select-none relative flex items-center h-[100px] w-full hover:bg-brand-grey-light px-6",
          { "bg-brand-purple-lightest hover:bg-brand-purple-lighter": !notification.isArchived }
        )}
      >
        <div className="text-start flex flex-col gap-1 w-full min-w-0">
          <div className="w-full flex justify-between">
            <h3 className="select-none overflow-hidden whitespace-nowrap text-ellipsis !pb-0">
              {notification.title}
            </h3>
            <div>{timeSince(new Date(notification.createdDate))}</div>
          </div>

          <p className="select-none overflow-hidden whitespace-pre text-ellipsis max-h-[50px]">
            {notification.body}
          </p>
        </div>
      </button>
    );
  }

  function SectionHeader({ title }: { title: string }) {
    return (
      <div className="first:pt-4 px-6 pb-2 pt-8 font-bold text-brand-grey-dark">
        {title}
      </div>
    );
  }

  const groups = groupNotifications(visibleData);

  return (
    <div data-testid="notifications-list">
      <div
        className={classNames(
          delayedLoading ? "h-[60px]" : "h-0",
          "pointer-events-none overflow-hidden transition-height duration-500 ease-in-out w-full flex items-center justify-center"
        )}
      >
        {delayedLoading && (
          <Spinner ariaLabel="" className="stroke-brand-purple" size="base" />
        )}
      </div>
      {groups.today.length > 0 && (
        <>
          <SectionHeader title={t("todayTitle")} />
          {groups.today.map(renderNotification)}
        </>
      )}

      {groups.week.length > 0 && (
        <>
          <SectionHeader title={t("thisWeekTitle")} />
          {groups.week.map(renderNotification)}
        </>
      )}

      {groups.earlier.length > 0 && (
        <>
          <SectionHeader title={t("earlierTitle")} />
          {groups.earlier.map(renderNotification)}
        </>
      )}
    </div>
  );
}
